import React, { useContext, useMemo } from 'react'
import { Button, Theme } from '@mui/material'
import FormattedPrice from '@components/FormattedPrice'
import { Trans, useTranslation } from 'react-i18next'
import ProductContext from '../context/ProductContext'
import { checkVariantAvailability, maybe } from '../../../utils/misc'
import { makeStyles } from '@mui/styles'
import { scroller } from 'react-scroll'
import QuantityInputField from './QuantityInputField'

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    position: 'fixed',
    bottom: 0,
    zIndex: 100,
    right: 32,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      right: 0,
    },
    '@media (min-width:1200px)': {
      right: 32,
    },
    '@media (min-width:1600px)': {
      right: 'calc((100vw - 1600px)/2 + 32px)',
    },
    '@media (min-width:1920px)': {
      right: 'calc((100vw - 1600px)/2 + 1px)',
    },
  },
  container: {
    borderRadius: '16px 16px 0 0',
    border: '1px solid #e7e7e7',
    borderBottom: 'none',
    background: '#fff',
    boxShadow: '0 -5px 16px #2f2f2f0f',
    display: 'flex',
    columnGap: '8px',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '72px',
    padding: '12px 16px',
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
      width: '100%',
      padding: '5px',
    },
  },
}))

const ProductFormFixedButtons: React.FC<{ width: number; scrollId: string }> = ({ width, scrollId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const {
    selectedVariant,
    formValues,
    onAddToBagFullKit,
    onAddToBag,
    onCustomizeKit,
    reflection,
    onDownloadFiles,
    formMethods,
  } = useContext(ProductContext)

  const attrName = useMemo(() => {
    const allAttrs = maybe(() => reflection.product.variantsProperties, [])
    return allAttrs[0]?.attributeName
  }, [reflection.product.variantsProperties])

  const isArchive = reflection?.product.isArchive
  const isInPack = reflection.product.isInPack
  const zipFileLink = reflection?.product.zipfileLink

  const product = reflection.product
  const priceRange = selectedVariant?.priceRange || product?.priceRange

  if (!priceRange) return null

  const isPriceRange = priceRange.discount.min !== priceRange.discount.max
  const quantity = formValues.quantity || 1
  const priceCurrency = priceRange.currency

  const sale = priceRange.discount.max < priceRange.undiscount.max

  const handleDownloadFiles = () => {
    onDownloadFiles(reflection?.product.zipfileLink)
  }
  const handleCheckVariantAvailability = () => checkVariantAvailability(selectedVariant)

  const scrollToElement = (name: string) => {
    const container =
      document.querySelector('.MuiDialog-container.MuiDialog-scrollBody .MuiDialog-paperFullScreen') ||
      document.querySelector('.MuiDialog-container.MuiDialog-scrollBody') ||
      document.querySelector('#product-details-drawer .MuiPaper-root')

    scroller.scrollTo(name, {
      duration: 600,
      delay: 0,
      offset: -600,
      smooth: true,
      container: container,
    })
  }

  const renderButtons = () => {
    if (!selectedVariant) {
      return (
        <div>
          {/*<ScrollLink to={scrollId} offset={-100} spy={true} smooth={true} duration={600}>*/}
          <Button
            variant='contained'
            color='secondary'
            size='large'
            onClick={() => scrollToElement(scrollId)}
            fullWidth
          >
            {!attrName ? t('button.addToBag', 'Add to bag') : `Choose ${attrName.toLowerCase()}`}
          </Button>
          {/*</ScrollLink>*/}
        </div>
      )
    }
    if (isInPack && !selectedVariant?.shippingRequired) {
      return (
        <div>
          {zipFileLink && (
            <Button variant='contained' color='secondary' size='large' onClick={handleDownloadFiles} fullWidth>
              {t('button.download', 'Download')}
            </Button>
          )}
          {!zipFileLink && (
            <Button variant='contained' color='secondary' size='large' onClick={handleDownloadFiles} fullWidth>
              {t('button.contactUsForMoreAttempts', 'Contact us for more attempts')}
            </Button>
          )}
        </div>
      )
    }
    const inBagDigital = selectedVariant.isInCart && !selectedVariant.shippingRequired
    const isSupply = selectedVariant.isSupply
    const isAvailable = handleCheckVariantAvailability()

    return (
      <div>
        {!isSupply && (
          <div>
            <Button
              variant='contained'
              color='secondary'
              size='large'
              onClick={onAddToBag}
              disabled={!isAvailable || inBagDigital || isArchive}
              disableElevation
              fullWidth
            >
              {isArchive && t('button.archive', 'Archive')}
              {!isArchive
                ? inBagDigital
                  ? t('button.alreadyInTheBag', 'Already in the bag')
                  : t('button.addToBag', 'Add to bag')
                : ''}
            </Button>
          </div>
        )}
        {isSupply && (
          <>
            {!selectedVariant.isCustomizable && (
              <Button
                variant='contained'
                color='secondary'
                size='large'
                onClick={onAddToBagFullKit}
                disabled={!isAvailable || isArchive}
                disableElevation
                fullWidth
              >
                <Trans
                  i18nKey='button.addToBagFullKitPrice'
                  defaults='Add to bag <price/>'
                  components={{
                    price: (
                      <FormattedPrice
                        currency={selectedVariant.priceRange.currency}
                        amount={selectedVariant.priceRange.discount.max}
                      />
                    ),
                  }}
                />
              </Button>
            )}
            {selectedVariant.isCustomizable && (
              <Button
                variant='contained'
                color='secondary'
                size='large'
                onClick={onCustomizeKit}
                disabled={!isAvailable || isArchive}
                disableElevation={!selectedVariant.hasDefaultKit}
                fullWidth
              >
                {t('button.customizeTheKit', 'Customize the KIT')}
              </Button>
            )}
          </>
        )}
      </div>
    )
  }
  const showQuantityField =
    selectedVariant &&
    !selectedVariant.isSupply &&
    selectedVariant.shippingRequired &&
    checkVariantAvailability(selectedVariant)

  return (
    <div className={classes.wrapper}>
      <div className={classes.container} style={{ minWidth: `${width}px` }}>
        <div>
          {showQuantityField ? (
            <QuantityInputField
              quantity={formValues.quantity}
              availableQuantity={selectedVariant.stockQuantity}
              onChangeQuantity={formMethods.onChangeQuantity}
              cartQuantity={selectedVariant.stockQuantity}
              canZero={false}
            />
          ) : (
            <div className='text-13 text-gray hidden sm:block'>
              {formValues.quantity} piece{formValues.quantity > 1 ? 's' : ''}
            </div>
          )}
        </div>
        <div className='flex items-center justify-between gap-16 flex-1 sm:flex-none'>
          <div>
            <div className='text-15 line-through'>
              {!!sale && (
                <>
                  {isPriceRange ? (
                    <>
                      <FormattedPrice currency={priceCurrency} amount={priceRange.undiscount.min * quantity} /> -{' '}
                      <FormattedPrice currency={priceCurrency} amount={priceRange.undiscount.max * quantity} />
                    </>
                  ) : (
                    <FormattedPrice currency={priceCurrency} amount={priceRange.undiscount.min * quantity} />
                  )}
                </>
              )}
            </div>
            <div className={`text-xl font-semibold ${!!sale ? 'text-red' : 'text-black'}`}>
              {isPriceRange ? (
                <>
                  <FormattedPrice currency={priceCurrency} amount={priceRange.discount.min * quantity} /> -{' '}
                  <FormattedPrice currency={priceCurrency} amount={priceRange.discount.max * quantity} />
                </>
              ) : (
                <FormattedPrice currency={priceCurrency} amount={priceRange.discount.min * quantity} />
              )}
            </div>
          </div>

          <div>{renderButtons()}</div>
        </div>
      </div>
    </div>
  )
}

export default ProductFormFixedButtons
